import type { OrganizationWorkflowCreatedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowCreated';
import type { OrganizationWorkflowDeactivatedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowDeactivated';
import type { OrganizationWorkflowOptionsSavedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowOptionsSaved';
import type { OrganizationWorkflowRestoredAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowRestored';
import type { OrganizationWorkflowSavedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowSaved';
import type { FacilityWorkflowsFetchFailedAction } from '@Application/Facilities/Workflows/Actions/facilityWorkflowsFetchFailed';
import type { FacilityWorkflowsFetchedAction } from '@Application/Facilities/Workflows/Actions/facilityWorkflowsFetched';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import FacilityWorkflowView from '@UI/Store/FacilityWorkflows/Views/FacilityWorkflowView';
import FacilityWorkflowsListView from '@UI/Store/FacilityWorkflows/Views/FacilityWorkflowsListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type FacilityWorkflowsStore = {
	facilityWorkflows: FacilityWorkflowsListView | null;
};

const initialState: FacilityWorkflowsStore = {
	facilityWorkflows: null,
};

type Actions =
	| FacilityWorkflowsFetchedAction
	| FacilityWorkflowsFetchFailedAction
	| OrganizationWorkflowCreatedAction
	| OrganizationWorkflowSavedAction
	| OrganizationWorkflowOptionsSavedAction
	| OrganizationWorkflowDeactivatedAction
	| OrganizationWorkflowRestoredAction
	| UserRevokedAction;

const whenFacilityWorkflowsFetched = (
	store: FacilityWorkflowsStore,
	action: FacilityWorkflowsFetchedAction,
): FacilityWorkflowsStore => ({
	...store,
	facilityWorkflows: new FacilityWorkflowsListView(
		action.payload.map((workflow) => new FacilityWorkflowView(workflow)),
		action.facilityId,
		action.subjectTypeKey,
	),
});

// A failure is carried by `useStoreQuery`'s own `error`: no second copy lives in the store.
const identity = (store: FacilityWorkflowsStore): FacilityWorkflowsStore => store;

// An organization's workflows changed: the proposed ones are read again on the next mount.
const whenOrganizationWorkflowChanged = (store: FacilityWorkflowsStore): FacilityWorkflowsStore => ({
	...store,
	facilityWorkflows: null,
});

const whenUserRevoked = (): FacilityWorkflowsStore => initialState;

export default registerStore<'facilityWorkflowsStore', Actions>(
	'facilityWorkflowsStore',
	createSliceReducer({
		FACILITY_WORKFLOWS_FETCHED: whenFacilityWorkflowsFetched,
		FACILITY_WORKFLOWS_FETCH_FAILED: identity,
		ORGANIZATION_WORKFLOW_CREATED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_SAVED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_OPTIONS_SAVED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_DEACTIVATED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_RESTORED: whenOrganizationWorkflowChanged,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
